import { useEffect, useMemo, type ReactNode } from 'react';
import { ikOphalen, meldSessieVerlopen } from './auth-api';
import { SpelContext } from './context';
import { SpelVerbinding, type SpelleiderSessie } from './verbinding';

type Props = {
  /** Het spel-id uit de route (`:gameId`). Een leeg of ongeldig id verbindt niet en geeft `onbekend`. */
  spelId: string;
  /**
   * Niet in het origineel: dit is een scherm van de spelleider. Heeft de verbinding geen
   * rechten (meer), dan gaat het scherm naar het inlogscherm of verbindt het opnieuw.
   */
  spelleider?: boolean;
  children?: ReactNode;
};

/** Wat de verbinding van een spelleider doet als de server zegt dat de rechten ontbreken. */
const SESSIE_VAN_SPELLEIDER: SpelleiderSessie = {
  isIngelogd: async () => (await ikOphalen()) !== null,
  opVerlopen: meldSessieVerlopen,
};

/**
 * Houdt een live verbinding met één spel open voor alles eronder. Vervangt de
 * Meteor-abonnementen van het origineel: lees de staat met `useSpel`, `useGame` en
 * `usePlayers`, voer wijzigingen uit met `useActie`.
 *
 * Een ander `spelId` geeft een nieuwe verbinding; de kinderen blijven staan.
 */
export function SpelProvider({ spelId, spelleider = false, children }: Props) {
  // Het maken opent nog niets, dus dit mag tijdens het renderen (en dubbel in StrictMode).
  const verbinding = useMemo(
    () =>
      new SpelVerbinding(spelId, spelleider ? { spelleider: SESSIE_VAN_SPELLEIDER } : undefined),
    [spelId, spelleider],
  );

  useEffect(() => {
    verbinding.start();
    return () => verbinding.stop();
  }, [verbinding]);

  return <SpelContext value={verbinding}>{children}</SpelContext>;
}
